import type { ColorConfig } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import {
  DEFAULT_BASE_COLOR,
  getPaletteSwatches,
  type PaletteSwatches,
} from '@/features/visualizations/utils/colors';
import type { VisualizationColorItem } from './types';

interface UseVisualizationColorItemsParams {
  orderedFieldValues: string[];
  colorConfig: ColorConfig | null | undefined;
  paletteSwatches?: PaletteSwatches;
  labelMap: Record<string, string>;
  valueToColorIndex?: Record<string, number>;
}

function resolveItemColor(paletteSwatches: PaletteSwatches, index: number): string {
  const palette = paletteSwatches.itemColors;
  if (palette.length === 0) return DEFAULT_BASE_COLOR;
  return palette[index % palette.length] ?? DEFAULT_BASE_COLOR;
}

export function useVisualizationColorItems({
  orderedFieldValues,
  colorConfig,
  paletteSwatches,
  labelMap,
  valueToColorIndex,
}: UseVisualizationColorItemsParams): VisualizationColorItem[] {
  return useMemo(() => {
    if (colorConfig?.mode === 'baseColor') return [];

    const resolvedPaletteSwatches = paletteSwatches ?? getPaletteSwatches(colorConfig);

    return orderedFieldValues.map((value, index) => {
      const colorIndex = valueToColorIndex?.[value] ?? index;
      return {
        id: value,
        color: resolveItemColor(resolvedPaletteSwatches, colorIndex),
        label: labelMap[value] || value,
      };
    });
  }, [orderedFieldValues, colorConfig, paletteSwatches, labelMap, valueToColorIndex]);
}
